import { useEffect, useRef } from 'react';

/**
 * Hook that animates layout position changes using the FLIP technique
 * (First, Last, Invert, Play)
 * @param dependencies - Array of dependencies that trigger layout changes
 * @returns Ref to attach to the element that should be animated
 */
export const useLayoutAnimationHorizontalDirection = <T extends HTMLElement>(dependencies: unknown[]): React.RefObject<T> => {
  const elementRef = useRef<T>(null);
  const previousPositionRef = useRef<{ x: number } | null>(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (!elementRef.current) return;

    // Skip animation on first render
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      // Measure position after initial render
      requestAnimationFrame(() => {
        if (elementRef.current) {
          const rect = elementRef.current.getBoundingClientRect();
          previousPositionRef.current = { x: rect.left };
        }
      });
      return;
    }

    // Store previous position before layout changes
    const previousPosition = previousPositionRef.current;
    let timeoutId: NodeJS.Timeout | null = null;

    // Wait for layout to update, then measure new position
    const rafId = requestAnimationFrame(() => {
      if (!elementRef.current) return;

      const element = elementRef.current;
      const currentRect = element.getBoundingClientRect();
      const currentPosition = { x: currentRect.left };

      if (!previousPosition) {
        previousPositionRef.current = currentPosition;
        return;
      }

      // Calculate the difference
      const deltaX = previousPosition.x - currentPosition.x;

      // If there's no significant change, skip animation
      if (Math.abs(deltaX) < 1) {
        previousPositionRef.current = currentPosition;
        return;
      }

      // Apply transform to move element back to previous position
      element.style.transform = `translateX(${deltaX}px)`;
      element.style.transition = 'none';

      // Force reflow to ensure the transform is applied
      // oxlint-disable-next-line @typescript-eslint/no-unused-expressions
      element.offsetHeight;

      // Remove transform and enable transition to animate to new position
      requestAnimationFrame(() => {
        if (elementRef.current) {
          elementRef.current.style.transition = 'transform 0.3s ease-in-out';
          elementRef.current.style.transform = '';
        }
      });

      // Update previous position after animation completes
      timeoutId = setTimeout(() => {
        if (elementRef.current) {
          previousPositionRef.current = currentPosition;
          elementRef.current.style.transition = '';
        }
      }, 300);
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return elementRef;
};
